import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';

// Local images
import dunkinIcreamProtein from './blogimg/dunkinicream.jpg';
import highProteinMealsImage from './blogimg/highproteinmeals.jpg';
import pancakeMixImage from './blogimg/pancakemix.jpg';
import chickenSaladImage from './blogimg/lunchChickenSalad.jpg';
import stirFryImage from './blogimg/stirFry.jpg';
import smoothieImage from './blogimg/smoothie.jpg';

import './blogcss/recipe.css';

/** 
 * Helper to map filenames from Firestore to local images.
 */
function getLocalImageSource(filename) {
  const cleanName = filename.trim().toLowerCase();
  switch (cleanName) {
    case 'dunkinicream.jpg':
      return dunkinIcreamProtein;
    case 'highproteinmeals.jpg':
      return highProteinMealsImage;
    case 'pancakemix.jpg':
      return pancakeMixImage;
    case 'lunchchickensalad.jpg':
      return chickenSaladImage;
    case 'stirfry.jpg':
      return stirFryImage;
    case 'smoothie.jpg':
      return smoothieImage;
    default:
      return null;
  }
}

const SportNutritionPage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "Sports Nutrition for Muscle & Strength | High-Protein Meal Ideas";

    // Helper function to add or update a meta tag
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      // If no existing meta tag, create one
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags (description, keywords)
    setMetaTag(
      "description",
      "Discover sports nutrition and high-protein meal ideas to fuel muscle growth. Learn how to use whey protein in breakfast, lunch, dinner, and snacks."
    );
    setMetaTag(
      "keywords",
      "Sports Nutrition, high-protein meals, whey protein, muscle growth, strength, meal ideas, Pure Health Nexus"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "Sports Nutrition & Whey Protein: High-Protein Meals for Muscle Growth",
      true
    );
    setMetaTag(
      "og:description",
      "Explore top-quality whey protein supplements and high-protein recipes for boosting muscle mass and recovery.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/assets/images/dunkinicream.jpg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/high-protein-meals",
      true
    );

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Cleanup when unmounting (optional)
    return () => {
      document.title = "Pure Health Nexus"; 
      // If you want to remove meta tags on unmount, do so here.
    };
  }, []);

  // 2️⃣ Fetch Your Article from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const colRef = collection(db, 'products', 'blog', 'sportnutrition');
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp);
        } else {
          // console.warn('No documents found in /sportnutrition');
        }
      } catch (error) {
        // console.error('Error fetching sport nutrition articles:', error);
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  let hasRenderedH1 = false;
  let hasRenderedFirstH2 = false;

  /**
   * Renders each block from Firestore. If the section title is "Instructions", 
   * we render any 'ul' as an ordered list (<ol>). Otherwise, we stick to the original format.
   */
  const renderBlock = (block, index, sectionTitle) => {
    const { tag, text } = block;

    // If section title is "Instructions" and the tag is 'ul', render as <ol>.
    if (
      sectionTitle &&
      sectionTitle.trim().toLowerCase() === 'instructions' &&
      tag === 'ul'
    ) {
      return (
        <ol key={index} className="recipe-ol">
          {Array.isArray(text)
            ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
            : <li>{text}</li>
          }
        </ol>
      );
    }

    switch (tag) {
      case 'h1':
        // Only render the first H1 to avoid duplicates
        if (hasRenderedH1) return null;
        hasRenderedH1 = true;
        return (
          <h1 key={index} className="recipe-h1">
            {text}
          </h1>
        );

      case 'h2':
        // Insert <hr> before every H2 after the first one
        if (!hasRenderedFirstH2) {
          hasRenderedFirstH2 = true;
          return (
            <h2 key={index} className="recipe-h2">
              <span>{text}</span>
            </h2>
          );
        }
        return (
          <React.Fragment key={index}>
            <hr
              style={{
                width: '75%',
                margin: '20px auto',
                border: '1px solid #e67e00',
              }}
            />
            <h2 className="recipe-h2">
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );

      case 'h3':
        return (
          <h3 key={index} className="recipe-h3">
            {text}
          </h3>
        );

      case 'h4':
        return (
          <h4 key={index} className="recipe-h4">
            {text}
          </h4>
        );

      case 'p':
        return (
          <p key={index} className="recipe-p">
            {text}
          </p>
        );

      case 'img':
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg || text}
              alt={text}
              style={{ maxHeight: '600px', width: 'auto' }}
            />
          </div>
        );

      case 'ul':
        return (
          <ul key={index} className="recipe-ul">
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>
            }
          </ul>
        );

      case 'ol':
        return (
          <ol key={index} className="recipe-ol">
            {Array.isArray(text)
              ? text.map((li, liIndex) => (
                  <li key={liIndex}>{li}</li>
                ))
              : <li>{text}</li>
            }
          </ol>
        );

      default:
        return <div key={index}>{text}</div>;
    }
  };
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  // 3️⃣ Render the Page Content
  return (
    <div className="container">
      <div className="high-protein-meals">
        {/* Primary H1 from Firestore data */}
        <h1 className="recipe-h1" style={{ position: 'relative' }}>
          {article.h1}
          <style>{`.recipe-h1::after { left: 8%; }`}</style>
        </h1>
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}
        {/* Sections from Firestore (e.g., paragraphs, images, lists, etc.) */}
        {article.sections.map((section, sectionIndex) => (
          <section key={sectionIndex}>
            {section.blocks.map((block, blockIndex) =>
              renderBlock(block, blockIndex, section.title)
            )}
          </section>
        ))}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() =>
            (window.location.href =
              'https://purehealthnexus.com/best-whey-protein')
          }
        >
          See Sports Nutrition Here
        </button>
      </div>
    </div>
  );
};

export default SportNutritionPage;
